import { isAllowedMediaUrl } from "./mediaUrlAllowlist";
import type { AvatarFallback, AvatarVisual, EntityAvatarProps } from "./EntityAvatar.types";

/** Very dark tag colors take the dark role glyph; white would vanish on them. */
const DARK_ROLE_TAG_COLORS = new Set(["#000000", "#003b80"]);

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

function safeColor(value: string | null | undefined): string | undefined {
  return typeof value === "string" && HEX_COLOR.test(value) ? value : undefined;
}

function safePhoto(value: string | null | undefined): string | undefined {
  return typeof value === "string" && isAllowedMediaUrl(value) ? value : undefined;
}

/** First letters of the first two words, uppercased. */
export function entityInitials(name: string | null | undefined): string {
  return (name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
}

/** Phone numbers and emails make meaningless initials; those people get the silhouette. */
function isContactAddress(name: string): boolean {
  return name.includes("@") || /^[+\d\s().-]+$/.test(name);
}

function personVisual(
  title: string | null | undefined,
  avatar: string | null | undefined,
): AvatarVisual {
  const name = title?.trim() ?? "";
  const initials = name && !isContactAddress(name) ? entityInitials(name) : "";
  const fallback: AvatarFallback = initials
    ? { kind: "initials", text: initials, tone: "initials" }
    : { kind: "glyph", glyph: "user", tone: "user" };
  return { shape: "circle", photo: safePhoto(avatar), fallback };
}

function roleVisual(
  tagColor: string | null | undefined,
  tagIsDefault: boolean | undefined,
): AvatarVisual {
  const color = safeColor(tagColor);
  if (!color || tagIsDefault) {
    return { shape: "rounded", fallback: { kind: "glyph", glyph: "role", tone: "roleDefault" } };
  }
  const tone = DARK_ROLE_TAG_COLORS.has(color.toLowerCase()) ? "roleOnDarkTag" : "roleOnTag";
  return { shape: "rounded", fallback: { kind: "glyph", glyph: "role", tone, tagColor: color } };
}

/**
 * Which avatar a conversation's counterparty gets, following TigerConnect's
 * avatar conventions:
 *
 * - People: uploaded photo, else initials, else the silhouette.
 * - Role conversations show the other party (a role badge or a person).
 * - Groups: uploaded photo over a default glyph for their kind; patient care,
 *   tagged activated teams, and escalated groups always show the glyph.
 * - Intra teams: initials on the team's tag color.
 */
export function resolveEntityAvatar({
  entityType,
  groupType,
  title,
  avatar,
  tagColor,
  isEscalated,
  recipient,
}: EntityAvatarProps): AvatarVisual {
  if (entityType === "account") {
    return personVisual(title, avatar);
  }
  if (entityType === "distlist") {
    return {
      shape: "circle",
      photo: safePhoto(avatar),
      fallback: { kind: "image", glyph: "distributionList" },
    };
  }

  if (groupType === "ROLE_P2P" && recipient) {
    return recipient.entity_type === "role"
      ? roleVisual(recipient.tag_color, recipient.tag_is_default)
      : personVisual(recipient.title, recipient.avatar);
  }

  const color = safeColor(tagColor);
  // An active escalation always shows the group's default glyph, never its photo.
  const photo = isEscalated ? undefined : safePhoto(avatar);

  switch (groupType) {
    case "INTRA_TEAM": {
      const text = entityInitials(title);
      if (!text) {
        return {
          shape: "circle",
          fallback: color
            ? { kind: "glyph", glyph: "activatedTeam", tone: "tag", tagColor: color }
            : { kind: "image", glyph: "teamUntagged" },
        };
      }
      return {
        shape: "circle",
        fallback: color
          ? { kind: "initials", text, tone: "tag", tagColor: color }
          : { kind: "initials", text, tone: "teamUntagged" },
      };
    }
    case "ACTIVATED_TEAM":
      return color
        ? {
            shape: "circle",
            fallback: { kind: "glyph", glyph: "activatedTeam", tone: "tag", tagColor: color },
          }
        : { shape: "circle", photo, fallback: { kind: "image", glyph: "teamUntagged" } };
    case "PATIENT_CARE":
      return {
        shape: "circle",
        fallback: { kind: "glyph", glyph: "patientCare", tone: "patientCare" },
      };
    case "ESCALATION":
      return { shape: "circle", photo, fallback: { kind: "image", glyph: "escalation" } };
    case "FORUM":
      return { shape: "circle", photo, fallback: { kind: "glyph", glyph: "forum", tone: "forum" } };
    default:
      // GROUP, and any kind with no distinct look of its own.
      return { shape: "circle", photo, fallback: { kind: "glyph", glyph: "group", tone: "group" } };
  }
}
